//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import PersonCategoryModel from './personCategory.model';

/**
 * @service PersonCategoryService
 * @description Service for managing Person Categories within the Trading Community module.
 * Person Categories define the classification of individuals within the system, such as 'Farmer', 'Dealer', 'Technician', etc.
 *
 * This service provides the interface to retrieve these definitions from the backend API.
 * It is essential for:
 * 1. **User Classification**: Categorizing users during registration or profile management.
 * 2. **Role-Based Logic**: Although distinct from roles, categories often imply certain business functions or attributes.
 * 3. **UI Dropdowns**: Populating selection lists for user forms.
 *
 * The service extends the generic `Service` class to inherit standard CRUD capabilities and HTTP handling,
 * specifically targeting the `/tradingcommunity/personcategory/` endpoint.
 *
 * @feature 1. Retrieve all defined Person Categories.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure comprehensive lists.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<PersonCategoryModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /tradingcommunity/personcategory/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PersonCategoryService extends Service<PersonCategoryModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/tradingcommunity/personcategory/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/personcategory/');
  }

  /**
   * @method getAllPersonCategory
   * @async
   * @description Fetch all person category records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all categories are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<PersonCategoryModel>>} Promise resolving to an array of PersonCategoryModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * personCategoryService.getAllPersonCategory().then(categories => {
   *   console.log('Person Categories:', categories);
   * });
   */
  public getAllPersonCategory() {
    return new Promise<Array<PersonCategoryModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonCategoryModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
